import { useEffect, useState } from 'react';
import { doc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';

interface AeReminderRunDoc {
  startedAtMs?: number;
  finishedAtMs?: number;
  cohortAccountsScanned?: number;
  cohortOppsScanned?: number;
  oppsRemindable?: number;
  aesNotified?: number;
  aesSkippedNoSlack?: number;
  ownersMissingSnapshot?: string[];
  slackFailures?: number;
  sent?: boolean;
  /**
   * When set, every reminder DM in this run was routed to this email
   * instead of the actual opportunity owner. Rollout-only artifact; will
   * be `null` once the override is removed from the orchestrator.
   */
  recipientOverrideEmail?: string | null;
  recipientOverrideSlackUserId?: string | null;
  /** StageName -> count across the full cohort scanned this run. */
  stageHistogram?: Record<string, number>;
  startedAt?: { toDate: () => Date };
  finishedAt?: { toDate: () => Date };
}

interface SendResult extends AeReminderRunDoc {
  ok: boolean;
}

// Mirrors `normalizeStage` in functions/src/reminders/rules.ts.
// Used only to grey-out skipped stages in the histogram detail.
const REMINDABLE_STAGES = new Set([
  'pre-qual',
  'discovery',
  'qualification',
  'evaluation',
  'proposal',
]);

function fmtRelative(date: Date): string {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Settings-page card for the Mon/Wed AE Slack reminders. Shows the most
 * recent run stats from `meta/aeReminderLastRun` and exposes a "Send now"
 * button (with optional Dry run) bound to the `sendAeRemindersNow`
 * callable.
 */
export function AeRemindersPanel() {
  const [last, setLast] = useState<AeReminderRunDoc | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<SendResult | null>(null);
  const [open, setOpen] = useState(false);
  const [dryRun, setDryRun] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'meta', 'aeReminderLastRun'),
      (snap) => {
        setLast(
          snap.exists() ? ((snap.data() as DocumentData) as AeReminderRunDoc) : null,
        );
        setLoaded(true);
      },
      (err) => {
        setError(err.message);
        setLoaded(true);
      },
    );
    return unsub;
  }, []);

  const onSend = async () => {
    setBusy(true);
    setError(null);
    setRecent(null);
    try {
      const fn = httpsCallable<{ dryRun?: boolean }, SendResult>(
        functions,
        'sendAeRemindersNow',
      );
      const res = await fn({ dryRun });
      setRecent(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const finishedAt = last?.finishedAt?.toDate();

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-800">
            AE Slack reminders
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Bi-weekly nudges (Mon + Wed at 14:00 UTC) DM each opportunity owner
            with their open pipeline grouped by stage and the next-step
            playbook.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {finishedAt
              ? `Last run finished ${fmtRelative(finishedAt)} · ${finishedAt.toLocaleString()}`
              : loaded
                ? 'No reminder run recorded yet.'
                : 'Loading…'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setError(null);
            setRecent(null);
          }}
          disabled={busy}
          className="btn-primary whitespace-nowrap"
        >
          {busy ? 'Sending…' : 'Send now'}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {(recent ?? last)?.recipientOverrideEmail && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <span className="font-semibold">Rollout override active</span> — all
          reminder DMs are being routed to{' '}
          <span className="font-mono">{(recent ?? last)?.recipientOverrideEmail}</span>{' '}
          instead of the opportunity owners. Each message is prefixed with the
          intended AE so the recipient can verify per-AE content.
        </div>
      )}

      {(recent ?? last) && (
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-4">
          <Stat label="Cohort accounts" value={(recent ?? last)?.cohortAccountsScanned ?? 0} />
          <Stat label="Open opps scanned" value={(recent ?? last)?.cohortOppsScanned ?? 0} />
          <Stat label="Remindable opps" value={(recent ?? last)?.oppsRemindable ?? 0} />
          <Stat
            label="AEs notified"
            value={(recent ?? last)?.aesNotified ?? 0}
            accent={(recent ?? last)?.aesNotified ? 'emerald' : undefined}
          />
          <Stat
            label="No Slack match"
            value={(recent ?? last)?.aesSkippedNoSlack ?? 0}
            accent={(recent ?? last)?.aesSkippedNoSlack ? 'amber' : undefined}
          />
          <Stat
            label="Slack failures"
            value={(recent ?? last)?.slackFailures ?? 0}
            accent={(recent ?? last)?.slackFailures ? 'rose' : undefined}
          />
          <Stat
            label="Mode"
            value={(recent ?? last)?.sent === false ? 'dry run' : 'sent'}
          />
        </div>
      )}

      {(recent ?? last)?.ownersMissingSnapshot &&
        ((recent ?? last)?.ownersMissingSnapshot?.length ?? 0) > 0 && (
          <p className="mt-3 text-xs text-amber-700">
            {((recent ?? last)?.ownersMissingSnapshot?.length ?? 0)} opportunity
            owner{((recent ?? last)?.ownersMissingSnapshot?.length ?? 0) === 1
              ? ''
              : 's'}{' '}
            were missing from the SF user snapshot. Re-run the daily SF sync to
            backfill, or check that these users still exist + are active.
          </p>
        )}

      {(recent ?? last)?.stageHistogram &&
        Object.keys((recent ?? last)?.stageHistogram ?? {}).length > 0 && (
          <details className="mt-3 text-xs text-slate-600">
            <summary className="cursor-pointer font-medium text-slate-700">
              Cohort by stage (
              {Object.values((recent ?? last)?.stageHistogram ?? {}).reduce(
                (a, b) => a + b,
                0,
              )}{' '}
              opps)
            </summary>
            <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              {Object.entries((recent ?? last)?.stageHistogram ?? {})
                .sort(([, a], [, b]) => b - a)
                .map(([stage, count]) => {
                  const remindable = REMINDABLE_STAGES.has(stage.toLowerCase());
                  return (
                    <li key={stage} className="flex items-center justify-between gap-2">
                      <span
                        className={
                          remindable ? 'text-slate-700' : 'text-slate-400'
                        }
                      >
                        {stage}
                        {!remindable && (
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
                            skipped
                          </span>
                        )}
                      </span>
                      <span className="font-mono tabular-nums text-slate-700">
                        {count}
                      </span>
                    </li>
                  );
                })}
            </ul>
            <p className="mt-2 text-[11px] text-slate-500">
              "Skipped" stages are intentionally excluded by the playbook
              (Negotiation+ — "no need to chase").
            </p>
          </details>
        )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="card w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-base font-semibold text-slate-900">
              Send AE reminders now?
            </h4>
            <p className="mt-1 text-sm text-slate-600">
              Each AE with at least one Discovery / Evaluation / Proposal opp
              will receive a Slack DM. AEs with no remindable opps are skipped.
            </p>

            <label className="mt-4 flex items-start gap-2 rounded-md border border-slate-200 p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              <span>
                <span className="font-medium text-slate-800">Dry run</span>
                <span className="block text-xs text-slate-500">
                  Build messages and audit the run, but do NOT actually post to
                  Slack. Useful for proofing rule changes.
                </span>
              </span>
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await onSend();
                  setOpen(false);
                }}
                disabled={busy}
                className="btn-primary"
              >
                {busy ? 'Sending…' : dryRun ? 'Run dry-run' : 'Send DMs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: 'rose' | 'emerald' | 'amber';
}) {
  const accentCls =
    accent === 'rose'
      ? 'text-rose-700'
      : accent === 'emerald'
        ? 'text-emerald-700'
        : accent === 'amber'
          ? 'text-amber-700'
          : 'text-slate-900';
  return (
    <div>
      <div className="label">{label}</div>
      <div className={`mt-0.5 text-sm font-semibold tabular-nums ${accentCls}`}>
        {value}
      </div>
    </div>
  );
}
